import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiMessageCircle, FiX, FiSend, FiRefreshCw } from 'react-icons/fi';
import { supportAPI, SupportChatMessage, SupportBotConfigPublic } from '../services/api';
import './SupportChatWidget.css';

interface SupportChatWidgetProps {
  isAuthenticated: boolean;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const SupportChatWidget: React.FC<SupportChatWidgetProps> = ({ isAuthenticated }) => {
  const location = useLocation();
  const [config, setConfig] = useState<SupportBotConfigPublic | null>(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  const loadConfig = useCallback(async () => {
    try {
      const data = await supportAPI.getConfig();
      setConfig(data);
    } catch {
      setConfig({
        bot_name: 'NAATI AI',
        is_enabled: true,
        welcome_message:
          "Hi! I'm NAATI AI, your support assistant. Ask me about subscriptions, mock credits, or practice materials.",
        show_on_public_pages: true,
      });
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (open && messages.length === 0 && config?.welcome_message) {
      setMessages([
        {
          id: -1,
          role: 'assistant',
          content: config.welcome_message,
          sources: [],
          created_at: new Date().toISOString(),
        },
      ]);
    }
  }, [open, config?.welcome_message, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setError(null);
    setLoading(true);

    const optimistic: SupportChatMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      sources: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const res = await supportAPI.sendMessage(text, conversationId ?? undefined);
      setConversationId(res.conversation_id);
      setMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m.id !== optimistic.id);
        return [...withoutOptimistic, res.user_message, res.assistant_message];
      });
    } catch (err: unknown) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setError(err instanceof Error ? err.message : 'Could not send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setConversationId(null);
    setMessages(
      config?.welcome_message
        ? [
            {
              id: -1,
              role: 'assistant',
              content: config.welcome_message,
              sources: [],
              created_at: new Date().toISOString(),
            },
          ]
        : []
    );
    setError(null);
  };

  if (isAdminRoute || isAuthPage) return null;
  if (config && !config.is_enabled) return null;
  if (config && !isAuthenticated && config.show_on_public_pages === false) return null;

  const botName = config?.bot_name || 'NAATI AI';

  return (
    <div className="naati-ai-widget" aria-label={`${botName} support chat`}>
      {open && (
        <div className="naati-ai-panel" role="dialog" aria-modal="true" aria-labelledby="naati-ai-title">
          <div className="naati-ai-header">
            <div className="naati-ai-header-info">
              <h3 id="naati-ai-title">{botName}</h3>
              <p>Support assistant</p>
            </div>
            <div className="naati-ai-header-actions">
              <button type="button" className="naati-ai-icon-btn" onClick={handleNewChat} title="New chat">
                {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
              </button>
              <button type="button" className="naati-ai-icon-btn" onClick={() => setOpen(false)} title="Close">
                {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 18 })}
              </button>
            </div>
          </div>

          <div className="naati-ai-messages">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`naati-ai-msg naati-ai-msg-${msg.role === 'user' ? 'user' : 'assistant'}`}
              >
                {msg.content}
                {msg.id > 0 && (
                  <div className="naati-ai-msg-time">{formatTime(msg.created_at)}</div>
                )}
              </div>
            ))}
            {loading && <div className="naati-ai-typing">NAATI AI is typing…</div>}
            <div ref={messagesEndRef} />
          </div>

          <div className="naati-ai-footer">
            {error && <p className="naati-ai-error">{error}</p>}
            <div className="naati-ai-input-row">
              <textarea
                ref={inputRef}
                className="naati-ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about subscriptions, mock credits…"
                rows={1}
                maxLength={2000}
                disabled={loading}
              />
              <button
                type="button"
                className="naati-ai-send"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                title="Send"
              >
                {React.createElement(FiSend as React.ComponentType<{ size?: number }>, { size: 18 })}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="naati-ai-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? `Close ${botName}` : `Open ${botName}`}
      >
        {open
          ? React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })
          : React.createElement(FiMessageCircle as React.ComponentType<{ size?: number }>, { size: 24 })}
        {!open && <span className="naati-ai-fab-label">{botName}</span>}
      </button>
    </div>
  );
};

export default SupportChatWidget;
